import { ContextMenu, ContextMenuItem } from "@/components/ui";
import type { MatterTemplate } from "@/lib/types";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { CopyPlus, Download, Pencil } from "lucide-react";
import type { ReactNode } from "react";

type MatterTemplateContextMenuProps = {
  canCreate: boolean;
  canDelete: boolean;
  canUpdate: boolean;
  items: MatterTemplate[];
  onDelete: (items: MatterTemplate[]) => void;
  onDownload: (items: MatterTemplate[]) => void;
  onDuplicate: (template: MatterTemplate) => void;
  onEdit: (template: MatterTemplate) => void;
  onEditDocument: (template: MatterTemplate) => void;
  trigger: ReactNode;
};

export function MatterTemplateContextMenu({
  canCreate,
  canDelete,
  canUpdate,
  items,
  onDelete,
  onDownload,
  onDuplicate,
  onEdit,
  onEditDocument,
  trigger,
}: MatterTemplateContextMenuProps) {
  const template = items[0];
  const isBulkAction = items.length > 1;
  const downloadableItems = items.filter((item) =>
    Boolean(item.presignedUrlKey),
  );
  const canEditDocument =
    canUpdate &&
    !isBulkAction &&
    getFileNameParts(template.name).fileExtension.toLowerCase() === "docx";

  return (
    <ContextMenu
      entityName="template"
      trigger={trigger}
      open={
        canUpdate && !isBulkAction
          ? {
              onSelect: () => {
                onEdit(template);
              },
            }
          : false
      }
      copy={
        !isBulkAction
          ? {
              label: "Copy template name",
              successLabel: "Template name",
              value: template.name,
            }
          : false
      }
      delete={
        canDelete
          ? {
              label: isBulkAction
                ? `Delete ${items.length} templates`
                : undefined,
              onSelect: () => {
                onDelete(items);
              },
            }
          : false
      }
      contentClassName="min-w-[220px]"
    >
      {downloadableItems.length > 0 ? (
        <ContextMenuItem
          onSelect={() => {
            onDownload(downloadableItems);
          }}
        >
          <Download />
          {isBulkAction
            ? `Download ${downloadableItems.length} templates`
            : "Download template"}
        </ContextMenuItem>
      ) : null}
      {canCreate && !isBulkAction && template.presignedUrlKey ? (
        <ContextMenuItem
          onSelect={() => {
            onDuplicate(template);
          }}
        >
          <CopyPlus />
          Duplicate template
        </ContextMenuItem>
      ) : null}
      {canEditDocument ? (
        <ContextMenuItem
          onSelect={() => {
            onEditDocument(template);
          }}
        >
          <Pencil />
          Edit document
        </ContextMenuItem>
      ) : null}
    </ContextMenu>
  );
}
